import type { AgentBillingAddSitesModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useModal } from "@v2/store/modalStore";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import { showAgentBillingUpdateSuccessToast } from "../agentBillingToasts";
import AddSubscriptionItemContent from "../components/AddSubscriptionItemContent";
import useAgentApplySubscriptionChangeMutation from "../hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "../hooks/useAgentSubscription";
import { useRateCards } from "../hooks/useRateCards";

const AgentBillingAddSitesModal: React.FC<
  AgentBillingAddSitesModalProps
> = () => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { data: reploSitesUsage } = workspaceRouter.getReploSitesUsage.useQuery(
    workspace?.id ?? skipToken,
  );

  const { applySubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  if (
    !workspace ||
    !activeSubscription ||
    !rateCards ||
    !currentDraft ||
    !reploSitesUsage ||
    reploSitesUsage.maximum === null ||
    !activeSubscription.currentPeriodEnd ||
    // (yuxin, 2026-07-14, REPL-29329): custom-contract add-ons go through support.
    activeSubscription.hasCustomPricing
  ) {
    return null;
  }

  const rateCard = rateCards.find(
    (rateCard) => rateCard.plan === activeSubscription.plan,
  );
  const siteProduct = rateCard?.addOnProducts.find(
    (product) => product.featureType === "sites",
  );

  if (!siteProduct) {
    return null;
  }

  const closeModal = () => {
    modal.closeModal({ type: "agentBillingAddSitesModal" });
  };

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      media={
        <img
          src="/images/billing/shop-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      <AddSubscriptionItemContent
        currentSubscriptionDraft={currentDraft}
        product={siteProduct}
        currentCount={reploSitesUsage.maximum}
        interval={activeSubscription.interval}
        currentPeriodEnd={activeSubscription.currentPeriodEnd}
        rateCards={rateCards}
        title={`Add Additional ${pluralize(siteProduct.displayName, 2)}`}
        onConfirm={async ({ subscriptionView, additionalCount }) => {
          try {
            analytics.logEvent("agentBilling.addSites.submitted", {
              currentPlan: activeSubscription.plan,
              additionalCount,
            });

            await applySubscriptionChange({
              workspaceId: workspace.id,
              subscriptionView,
              successUrl: window.location.href,
              cancelUrl: window.location.href,
            });

            showAgentBillingUpdateSuccessToast(
              `Added ${formatNumber(additionalCount)} additional ${pluralize(siteProduct.displayName, additionalCount)}`,
              { showConfetti: true },
            );
            closeModal();
          } catch (error) {
            const displayName = siteProduct.displayName.toLowerCase();
            const errorMessage =
              error instanceof Error
                ? error.message
                : `Failed to add ${displayName}`;
            toast.error({
              title: `Failed to add ${displayName}`,
              description: errorMessage,
            });
          }
        }}
        isLoading={isLoadingSubscriptionUpdate}
      />
    </SplitModal>
  );
};

export default AgentBillingAddSitesModal;
